/** 当日总览（屏规格 `docs/design/screens/overview.md`，#4598）。
 *
 * 三件事：账户今天怎么样（与当日日报同一个数，总览同源）、每日链路五步有没有跑齐、有没有需要人看的异常。
 * - 停机 / 对账：`GET /status`（与壳层顶栏同源）+ `GET /paper/state`（按策略）；
 * - 账户 / 持仓 / 敞口 / 异常项：最近一份日报 `GET /reports/daily/{date}`，不回落到 `/status` 的回测账；
 * - 每日链路：`GET /chain/daily`（服务端判定，前端不拼）；重跑 / 从断点续跑 `POST /chain/daily/run`（E4 #5007）；
 *   页头显示链路交易日（`trade_date`，服务端按交易日历判定；交易日判定本身只有命令，#5012）；
 * - 最近告警 `GET /alerts`、最近事件 `GET /events`（SSE，只取 8 条）。
 */

import { NavLink } from "react-router";

import { useStatus } from "../../api/queries";
import { Card, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Loading, QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { money, num, pct, signedMoney, tone } from "../../lib/format";
import { isStaleDataDate, latestReport, sortHeadline, type DailyReportView, type PaperStateView } from "../../lib/g1";
import { useAlertsLoose, useDailyChain, useEventStream, usePaperState, useReportIndex, useReportView } from "../../lib/g1Queries";
import type { StatusPayload } from "../../api/types";
import { ChainCard, EventTable, Load } from "./parts";

export function OverviewScreen(): React.ReactNode {
  const status = useStatus();
  const paper = usePaperState();
  const index = useReportIndex();
  const latest = index.data ? latestReport(index.data) : null;
  const report = useReportView(latest?.trade_date ?? null);
  const chain = useDailyChain();
  const alerts = useAlertsLoose();
  const stream = useEventStream(200);

  if (status.isPending) return <Loading what="系统状态" />;
  if (status.isError) {
    return (
      <QueryState status="error" error={status.error} what="系统状态" refetch={() => void status.refetch()}>
        {null}
      </QueryState>
    );
  }
  const s = status.data;
  const dataDate = latest?.trade_date ?? null;
  // 「非今日」按后端「最近已开跑交易日」比（#4813），T+1 早上数据日期 = T 不标。
  const stale = isStaleDataDate(dataDate, chain.data?.trade_date);

  return (
    <>
      <PageHead
        title="当日总览"
        sub={
          <>
            数据日期 {dataDate ?? "—"}
            {stale ? "（非今日）" : ""} · <span data-field="chain-trade-date">链路交易日 {chain.data?.trade_date ?? "—"}</span> · 口径 {s.config_digest}
          </>
        }
        actions={<Pill tone={s.halted ? "halt" : "ok"}>{s.halted ? "停机停手" : "允许下单"}</Pill>}
      />
      <Lede>账户数字与当日日报同出处、同口径（总览同源）；链路是否齐、对账是否平、是否停机都由后端判定后给出。</Lede>

      <div className="grid g23" style={{ marginBottom: 12 }}>
        <Card title="需要看的项" sub="GET /status · /paper/state · 日报 headline" nopad>
          <AttentionTable status={s} paper={paper.data ?? null} paperError={paper.isError} report={report.data ?? null} reportPending={Boolean(latest) && report.isPending} hasReport={Boolean(latest)} />
        </Card>
        <Load q={chain} what="每日链路">
          {(data) => <ChainCard chain={data} halted={s.halted || Boolean(paper.data?.halted)} />}
        </Load>
      </div>

      <Card title={`账户 / 持仓 / 敞口${dataDate ? ` · 数据日期 ${dataDate}${stale ? "（非今日）" : ""}` : ""}`} sub="GET /reports/daily/{date} · 权益口径为主" nopad>
        <Load q={index} what="日报索引">
          {(rows) =>
            rows.length === 0 ? (
              <Note>还没有日报，总览与日报同源，日报出来前这里不显示数字。</Note>
            ) : (
              <Load q={report} what={`${dataDate} 日报`}>
                {(data) => <AccountTable report={data} />}
              </Load>
            )
          }
        </Load>
      </Card>

      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="最近告警" sub="GET /alerts · 最近 7 天" nopad>
          <Load q={alerts} what="告警">
            {(rows) => (
              <Table
                cols={[{ label: "时间", width: "24%" }, { label: "类别", width: "22%" }, { label: "摘要", wrap: true }]}
                rows={rows.slice(-8).reverse().map((row, i) => ({ key: `${row.ts_utc}-${i}`, cells: [row.ts_utc.slice(0, 19).replace("T", " "), row.kind, row.summary] }))}
                empty="最近 7 天无告警"
              />
            )}
          </Load>
        </Card>
        <Card title="最近事件" sub={`GET /events（SSE）· ${stream.state === "open" ? "已连接" : stream.state === "connecting" ? "连接中" : "重连中"} · 全量在「日志」`} nopad>
          {stream.error ? <Note tone="bad">{stream.error}</Note> : null}
          <EventTable rows={stream.rows.filter((r) => r.kind === "event").slice(0, 8)} empty="事件流暂无事件（服务重启后缓冲为空）" />
        </Card>
      </div>
    </>
  );
}

function AttentionTable({
  status,
  paper,
  paperError,
  report,
  reportPending,
  hasReport,
}: {
  status: StatusPayload;
  paper: PaperStateView | null;
  paperError: boolean;
  report: DailyReportView | null;
  reportPending: boolean;
  hasReport: boolean;
}): React.ReactNode {
  const haltedStrategies = paper ? Object.entries(paper.strategies).filter(([, item]) => item.halted) : [];
  const noRecon = status.balance.checked_run === null || status.balance.checked_run === undefined;
  const rows = [
    {
      key: "halt",
      cls: status.halted ? "bad" : "",
      cells: [
        "停机停手",
        status.halted ? <Pill tone="halt">停手</Pill> : <Pill tone="ok">未触发</Pill>,
        status.halted ? (
          <span>
            {status.halt_reason ?? "回测账未给原因"}
            {haltedStrategies.length ? ` · 纸上盘停机：${haltedStrategies.map(([sid, item]) => `${sid}（${item.halt_reason ?? "—"}）`).join("；")}` : ""}
            {" "}
            <NavLink to="/halt">去停机屏 →</NavLink>
          </span>
        ) : paperError ? (
          "按策略停机状态未取到"
        ) : (
          "恢复只走人工放行，须留放行人与理由"
        ),
      ],
    },
    {
      key: "recon",
      cls: !noRecon && !status.balance.balanced ? "bad" : "",
      cells: [
        "对账",
        noRecon ? <Pill tone="warn">还没对过账</Pill> : status.balance.balanced ? <Pill tone="ok">平</Pill> : <Pill tone="bad">不平</Pill>,
        noRecon ? "还没有任何运行，对账无从谈起（不是「平」）" : `运行 ${status.balance.checked_run} · 已核 ${num(status.balance.checked, 0)} 笔`,
      ],
    },
  ];
  const headline = report ? sortHeadline(report.headline) : [];
  const headRows = !hasReport
    ? [{ key: "no-report", cells: ["日报异常项", <Pill tone="neutral">无日报</Pill>, "还没有日报"] }]
    : reportPending || !report
      ? [{ key: "pending", cells: ["日报异常项", <Pill tone="neutral">读取中</Pill>, "—"] }]
      : headline.length === 0
        ? [{ key: "ok", cells: ["日报异常项", <Pill tone="ok">{report.status}</Pill>, "当日无异常项"] }]
        : headline.map((item, i) => ({
            key: `h-${i}`,
            cls: item.escalated ? "bad" : "",
            cells: [
              item.kind,
              item.escalated ? <Pill tone="bad">持续 {item.consecutive_days} 天</Pill> : <Pill tone="warn">{item.consecutive_days && item.consecutive_days > 1 ? `持续 ${item.consecutive_days} 天` : "首次"}</Pill>,
              `${item.strategy_id ? `${item.strategy_id} · ` : ""}${item.detail}`,
            ],
          }));
  return <Table cols={[{ label: "项", width: "20%" }, { label: "状态", width: "16%" }, { label: "说明", wrap: true }]} rows={[...rows, ...headRows]} />;
}

function AccountTable({ report }: { report: DailyReportView }): React.ReactNode {
  const entries = Object.entries(report.strategies);
  return (
    <>
      <Table
        cols={[
          { label: "策略" },
          { label: "总盈亏（权益）", num: true },
          { label: "已实现（平仓）", num: true },
          { label: "未实现", num: true },
          { label: "现金变动（对照）", num: true },
          { label: "手续费 + 滑点", num: true },
          { label: "持仓数", num: true },
          { label: "总敞口", num: true },
          { label: "敞口 / 起始", num: true },
          { label: "对账" },
        ]}
        rows={entries.map(([sid, item]) => ({
          key: sid,
          cls: item.no_run ? "warn" : item.reconciliation.balanced === false ? "bad" : "",
          cells: [
            sid,
            item.pnl.total_pnl === null ? "—" : <span className={tone(item.pnl.total_pnl)}>{signedMoney(item.pnl.total_pnl)}</span>,
            item.pnl.realized_closed === null ? "—" : signedMoney(item.pnl.realized_closed),
            item.pnl.unrealized === null ? "—" : signedMoney(item.pnl.unrealized),
            item.pnl.cash_change === null ? "—" : <span className="faint">{signedMoney(item.pnl.cash_change)}</span>,
            item.pnl.commissions === null && item.pnl.slippage_cost === null ? "—" : money((item.pnl.commissions ?? 0) + (item.pnl.slippage_cost ?? 0)),
            item.positions.length,
            item.exposure.gross === null ? "—" : money(item.exposure.gross),
            item.exposure.as_pct_of_starting === null ? "—" : pct(item.exposure.as_pct_of_starting / 100),
            item.no_run ? <Pill tone="warn">当天没有 run</Pill> : item.reconciliation.balanced === null ? "—" : item.reconciliation.balanced ? <Pill tone="ok">平</Pill> : <Pill tone="bad">不平</Pill>,
          ],
        }))}
        empty="日报里没有策略分区"
      />
      <Note>主口径为权益口径（总盈亏 = 已实现 + 未实现）；现金变动是对照项，不是盈亏。</Note>
    </>
  );
}
